import * as Yup from 'yup';
import type { Flags, Maybe } from 'yup';
import * as actions from '../../users/store/actions';
import { store } from '../../../lib/redux';

// Типизация аддон-метода notEqualTo: подключается потребителями через
// Yup.addMethod(Yup.string, 'notEqualTo', notEqualTo) — см. clients/validation.ts.
// Без этой аугментации вызовы .notEqualTo(...) на StringSchema не тайпчекаются.
declare module 'yup' {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface StringSchema<TType extends Maybe<string>, TContext, TDefault, TFlags extends Flags> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    notEqualTo(ref: any, msg?: string): StringSchema<TType, TContext, TDefault, TFlags>;
  }
}

// Аддон-метод Yup: подключается через Yup.addMethod(Yup.string, 'notEqualTo', notEqualTo)
// (см. clients/validation.ts). `this` — схема, к которой применён метод.
export function notEqualTo(this: any, ref: any, msg?: string) {
  return this.test({
    name: 'notEqualTo',
    exclusive: false,
    // eslint-disable-next-line no-template-curly-in-string
    message: msg || '${path} must be not the same as ${reference}',
    params: {
      reference: ref.path,
    },
    test(value) {
      return !value || this.schema.cast(value) !== this.schema.cast(this.resolve(ref));
    },
  });
}

export const firstName = (required = true, message = 'Укажите имя') => {
  const validator = Yup.string()
    .min(2, 'Недопустимо короткое имя')
    .max(255, 'Максимальная длина 255 символов')
    .matches(/^[а-яё\s]+$/i, 'Поддерживается только кириллица');

  return required ? validator.required(message) : validator.nullable().notRequired();
};

export const lastName = (required = true, message = 'Укажите фамилию') => {
  const validator = Yup.string()
    .min(2, 'Недопустимо короткая фамилия')
    .max(255, 'Максимальная длина 255 символов')
    .matches(/^[а-яё\-\s]+$/i, 'Поддерживается только кириллица');

  return required ? validator.required(message) : validator.nullable().notRequired();
};

export const middleName = (required = true, message = 'Укажите отчество') => {
  const validator = Yup.string()
    .min(2, 'Недопустимо короткое отчество')
    .max(255, 'Максимальная длина 255 символов')
    .matches(/^[а-яё\-\s]+$/i, 'Поддерживается только кириллица');

  return required ? validator.required(message) : validator.nullable().notRequired();
};

export const email = (required = true, message = 'Укажите email') => {
  const validator = Yup.string().trim().email('Некорректный email');

  return required ? validator.required(message) : validator.nullable().notRequired();
};

export const uniqueEmail = (excludeUserId = false, required = true, message = 'Этот email уже используется!') => {
  return email(required).test('emailIsUnique', message, async testEmail => {
    const isValid = await email(required).isValid(testEmail);
    if (!isValid) return false;

    await store.dispatch(actions.checkEmail({
      email: testEmail,
      excludedUserId: excludeUserId ? store.getState().users?.item?.id : null,
    }));

    const { lastCheckedEmailResponse } = store.getState().users;
    return !lastCheckedEmailResponse.alreadyExist;
  });
};

export const password = (required = true, message = 'Введите пароль') => {
  const validator = Yup.string()
    .min(8, 'Пароль должен содержать не менее 8 символов, среди которых есть буквы и цифры')
    .matches(/[\d]+/, 'Пароль должен содержать хотя бы 1 цифру')
    .matches(/[a-zа-яё]+/, 'Пароль должен содержать хотя бы 1 букву в нижнем регистре')
    .matches(/[A-ZА-ЯЁ]+/, 'Пароль должен содержать хотя бы 1 букву в верхнем регистре');
    // .matches(/^((?![<>|:'"*?\\/\s]).)*$/, 'Пароль не должен содержать служебные символы <>|:\'"*?\\/ и пробел');

  return required ? validator.required(message) : validator.nullable().notRequired();
};

export const phone = (required = true, message = 'Укажите телефон') => {
  const validator = Yup.string()
    .strict(true)
    .transform(function (value) {
      if (this.isType(value) && (value !== null)) {
        const phoneNumber = value.trim().replace(/(\(|\)|-|_|\s)/g, '');

        return (phoneNumber.indexOf('+7') === -1)
          ? `+7${ phoneNumber }`
          : phoneNumber;
      }

      return value;
    })
    .min(10, 'Недопустимо короткий телефон')
    .matches(/^[\d\s()+-]+$/, 'Неверный формат телефона');

  return required ? validator.required(message) : validator.nullable().notRequired();
};
